import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
  type SignInOutput,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage } from 'aws-amplify/utils';
import type { RuntimeConfig } from '../config.js';
import type { Mfa } from './mfa.js';

/**
 * Eigene Anmeldung (Amplify Auth, SRP) statt Cognito Managed Login.
 *
 * Shell und Tools laufen unter einer Origin und nutzen denselben App-Client. Amplify legt die Tokens
 * im sessionStorage dieser Origin ab – die Tools lesen sie mit derselben Konfiguration mit, ohne
 * erneute Anmeldung. Gültig nur für die Dauer des Browser-Tabs.
 */

/** Wie geht es nach einem Anmeldeschritt weiter? */
export type SignInStep = 'done' | 'newPassword' | 'totp' | 'resetPassword';

export interface AuthClient {
  hasSession(): Promise<boolean>;
  signIn(email: string, password: string): Promise<SignInStep>;
  /** Erstanmeldung nach Einladung: eigenes Passwort statt des vorläufigen. */
  setNewPassword(password: string): Promise<SignInStep>;
  confirmTotp(code: string): Promise<SignInStep>;
  /** Passwort vergessen: Code per E-Mail anfordern. */
  requestReset(email: string): Promise<void>;
  confirmReset(email: string, code: string, password: string): Promise<void>;
  idToken(): Promise<string>;
  /** Neue Tokens mit aktuellen Claims (nach Rollen- oder MFA-Änderungen). */
  refresh(): Promise<void>;
  /** Abmelden auf allen Geräten (entzieht auch die Refresh-Tokens). */
  signOut(): Promise<void>;
}

export class AuthError extends Error {}

/** Cognito-Fehler in verständliche, neutrale Meldungen übersetzen (keine Hinweise, ob ein Konto existiert). */
export function authMessage(err: unknown): string {
  const e = err as { name?: string; message?: string };
  const msg = e.message ?? '';
  switch (e.name) {
    case 'NotAuthorizedException':
      if (/temporary password has expired/i.test(msg)) return 'Die Einladung ist abgelaufen. Bitte lass dich erneut einladen.';
      if (/attempts exceeded/i.test(msg)) return 'Zu viele Versuche. Bitte warte einen Moment und versuche es dann erneut.';
      if (/session is expired/i.test(msg)) return 'Die Anmeldung hat zu lange gedauert. Bitte von vorn beginnen.';
      return 'E-Mail oder Passwort ist falsch.';
    case 'UserNotFoundException':
      return 'E-Mail oder Passwort ist falsch.';
    case 'CodeMismatchException':
      return 'Der Code stimmt nicht. Bitte den aktuellen Code eingeben.';
    case 'ExpiredCodeException':
      return 'Der Code ist abgelaufen. Bitte einen neuen anfordern.';
    case 'InvalidPasswordException':
      return 'Das Passwort ist zu schwach: mindestens 12 Zeichen mit Groß- und Kleinbuchstaben und einer Zahl.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Zu viele Versuche. Bitte warte einen Moment und versuche es dann erneut.';
    case 'PasswordResetRequiredException':
      return 'Bitte setze zuerst dein Passwort zurück („Passwort vergessen?“).';
    case 'NetworkError':
      return 'Keine Verbindung. Bitte prüfe deine Internetverbindung.';
    default:
      return err instanceof AuthError ? msg : 'Das hat nicht geklappt. Bitte versuche es erneut.';
  }
}

function stepOf(out: SignInOutput): SignInStep {
  switch (out.nextStep.signInStep) {
    case 'DONE':
      return 'done';
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return 'newPassword';
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return 'totp';
    case 'RESET_PASSWORD':
      return 'resetPassword';
    default:
      // SMS, E-Mail-Codes, Passkeys u. Ä. sind im Pool nicht aktiviert.
      throw new AuthError('Diese Art der Anmeldung wird nicht unterstützt. Bitte wende dich an das Betriebsam-Team.');
  }
}

export function configureAmplify(cfg: RuntimeConfig): void {
  Amplify.configure({ Auth: { Cognito: { userPoolId: cfg.userPoolId, userPoolClientId: cfg.clientId, loginWith: { email: true } } } });
  cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
}

export function amplifyAuth(): AuthClient {
  return {
    async hasSession() {
      const s = await fetchAuthSession().catch(() => undefined);
      return Boolean(s?.tokens?.idToken);
    },
    async signIn(email, password) {
      // Reste einer abgelaufenen Sitzung würden „bereits angemeldet“ melden.
      await signOut().catch(() => undefined);
      return stepOf(await signIn({ username: email.trim().toLowerCase(), password }));
    },
    async setNewPassword(password) {
      return stepOf(await confirmSignIn({ challengeResponse: password }));
    },
    async confirmTotp(code) {
      return stepOf(await confirmSignIn({ challengeResponse: code.replace(/\s/g, '') }));
    },
    async requestReset(email) {
      await resetPassword({ username: email.trim().toLowerCase() });
    },
    async confirmReset(email, code, password) {
      await confirmResetPassword({ username: email.trim().toLowerCase(), confirmationCode: code.replace(/\s/g, ''), newPassword: password });
    },
    async idToken() {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      if (!token) throw new AuthError('Nicht angemeldet');
      return token;
    },
    async refresh() {
      await fetchAuthSession({ forceRefresh: true });
    },
    async signOut() {
      await signOut({ global: true }).catch(() => signOut());
    },
  };
}

/** MFA (TOTP) mit den eigenen Tokens über Amplify – Cognito prüft Identität und Code selbst. */
export function amplifyMfa(): Mfa {
  return {
    async status() {
      const pref = await fetchMFAPreference();
      return { enabled: Boolean(pref.enabled?.includes('TOTP')) };
    },
    async begin() {
      return (await setUpTOTP()).sharedSecret;
    },
    async verify(code) {
      try {
        await verifyTOTPSetup({ code: code.replace(/\s/g, ''), options: { friendlyDeviceName: 'Authenticator' } });
      } catch (err) {
        throw new Error(authMessage(err));
      }
      await updateMFAPreference({ totp: 'PREFERRED' });
    },
  };
}

/**
 * Ziel nach der Anmeldung: nur Pfade dieser Origin (keine offenen Weiterleitungen),
 * nicht zurück auf öffentliche Seiten.
 */
export function safeTarget(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return '/';
  if (/^\/(anmelden|impressum|datenschutz|kontakt)(\/|\?|$)/.test(raw)) return '/';
  return raw;
}

export const PUBLIC_PATHS = ['/anmelden', '/impressum', '/datenschutz', '/kontakt'];
export const isPublicPath = (path: string) => PUBLIC_PATHS.includes(path.replace(/\/$/, '') || '/');
